import React, { useEffect } from "react";
import {
  advertisingBannersApi,
  useAppSelector,
  api,
  useAppDispatch,
} from "@litres-core/store";
import { TM } from "@litres-libs/tracker-manager";

import { SkeletonBlock } from "@litres/ui-kit/skeleton";
import { SmartLink } from "@litres-modules/smart-link";
import type { AdvertisingTypes } from "@litres-libs/types";
import { getTopBannerPlacement, getWebpCoverURL } from "@litres/utils";
import { useTranslation } from "@litres-libs/i18n";
import { AdvertisingMarking } from "../../AdvertisingMarking/AdvertisingMarking";
import styles from "./MainPromoBanner.module.scss";

export const MainPromoBanner = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const headerHeight = useAppSelector(
    state => state.global.interface.headerHeight
  );
  const isPDA = useAppSelector(state => state.browser.pda);
  const faceLanguage = useAppSelector(state => state.face.language || "ru");

  const bannerPlacement = getTopBannerPlacement({ isPDA, faceLanguage });

  const { data, isLoading } =
    advertisingBannersApi.useGetBannersQuery(bannerPlacement);
  const headerBanner = data?.[0];

  const getEventData = (data: AdvertisingTypes.BannerResponse) => ({
    // документация по аналитике https://docs.litres.ru/pages/viewpage.action?pageId=98603566#id-%D0%91%D0%B0%D0%BD%D0%BD%D0%B5%D1%80%D1%8B%D0%BD%D0%B0%D0%9B%D0%B8%D1%82%D1%80%D0%B5%D1%81,MyBook%D0%B8Selfpub-%D0%90%D0%BD%D0%B0%D0%BB%D0%B8%D1%82%D0%B8%D0%BA%D0%B0
    ...data.event_data,
    block_type: "banners",
    block_title: data.banner_title,
    block_name: data.event_data.place_slug,
    ad_campaign_id: data.event_data.campaign_id,
  });

  useEffect(() => {
    if (headerBanner && !isLoading) {
      TM.sendEvent(["LITRES_TRACKER"], {
        eventType: "adv_banner_view",
        params: getEventData(headerBanner),
      });
    } else {
      //в случае если баннер не пришел, отнимаем от высоты хэдера высоту скелетона
      dispatch(api.global.interfaceRequests.setHeaderHeight(headerHeight - 50));
    }
  }, [headerBanner, isLoading]);

  const handleClick = () => {
    if (headerBanner) {
      TM.sendEvent(["LITRES_TRACKER"], {
        eventType: "adv_banner_click",
        params: getEventData(headerBanner),
      });
    }
  };

  if (isLoading) {
    return (
      <div className={styles.wrapper} role="none">
        <SkeletonBlock width="100%" height="100%" />
      </div>
    );
  }

  return headerBanner ? (
    <div
      className={styles.wrapper}
      role="presentation"
      aria-label={t("Реклама")}
    >
      <SmartLink
        className={styles.banner}
        href={headerBanner.transition_link}
        data-testid="mainPromoBanner--desktop"
        onClick={handleClick}
        target="_blank"
        rel="noreferrer"
        aria-label={headerBanner.banner_title}
      >
        <picture>
          <source
            srcSet={getWebpCoverURL(headerBanner.image_url)}
            type="image/webp"
          />
          <source srcSet={headerBanner.image_url} />
          <img
            className={styles.banner}
            src={headerBanner.image_url}
            alt={headerBanner.banner_title}
            decoding="sync"
          />
        </picture>
      </SmartLink>
      {headerBanner.advertising_marking && (
        <div className={styles.advertising}>
          <AdvertisingMarking
            link={headerBanner.transition_link}
            marking={headerBanner.advertising_marking}
            forBanner
          />
        </div>
      )}
    </div>
  ) : null;
};
